import React, { FormEventHandler, useState } from 'react';
import { z } from 'zod';

import PermissionTooltip from '@dashboard/components/PermissionTooltip';
import { alertError, alertSuccess, getApolloErrorMessage } from '@dashboard/contexts/SnackbarManager/store';
import {
  useCreateDomainNameVerificationMutation,
  useDomainNameVerificationsQuery,
} from '@dashboard/generated/wp/platform';
import usePlatformNavigator from '@dashboard/hooks/navigate/usePlatformNavigator';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';

import { usePermission } from '../hooks';

type FormProps = {
  onClose: () => void;
};

const STRING_SCHEMA = z
  //
  .string()
  .min(1, 'Domain name must not be empty')
  .refine((value) => !(value.startsWith('http:') || value.startsWith('https:')), {
    message: 'Must not use a protocol. eg: use example.com instead of http://example.com.',
  })
  .refine((value) => !value.includes('@'), {
    message: 'Cannot be an email address. Use a domain name instead.',
  })
  .refine((value) => !value.match(/:[\d]*$/), {
    message: 'Must not include port. eg: use example.com instead of example.com:8000.',
  });

function FormDialog({ onClose }: FormProps) {
  const [create, { loading }] = useCreateDomainNameVerificationMutation();
  const { refetch } = useDomainNameVerificationsQuery();
  const [domainName, setDomainName] = useState('');
  const navigator = usePlatformNavigator();

  const onSubmit: FormEventHandler<HTMLFormElement> = async (ev) => {
    ev.preventDefault();
    ev.stopPropagation();

    const validation = STRING_SCHEMA.safeParse(domainName);
    if (!validation.success) {
      alertError(validation.error.issues[0].message);
      return;
    }
    try {
      const { data } = await create({ variables: { domainName } });
      await refetch();
      const id = data?.createDomainNameVerification.id;
      if (id) {
        navigator.showDomainName(id);
        alertSuccess('Domain name verification added');
      }

      onClose();
    } catch (e) {
      const message = getApolloErrorMessage(e) ?? 'Error adding domain name';
      alertError(message);
    }
  };

  return (
    <Dialog open onClose={onClose}>
      <DialogTitle>Add a domain</DialogTitle>
      <form onSubmit={onSubmit}>
        <DialogContent>
          <Stack spacing={1}>
            <Typography paragraph>Which domain do you want to send emails from?</Typography>
            <TextField
              fullWidth
              autoFocus
              autoComplete="off"
              label="Domain name"
              margin="normal"
              value={domainName}
              onChange={(ev) => setDomainName(ev.target.value)}
              helperText="Examples: my-domain.com, mail.my-domain.com, my-domain-mail.com"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={loading} onClick={onClose} color="secondary">
            Cancel
          </Button>
          <Button disabled={loading} type="submit" variant="contained">
            Add domain
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default function CreateDomainNameVerification() {
  const [open, setOpen] = useState(false);
  const permission = usePermission('createVerifiedDomain');

  let dialog = null;
  if (open) {
    dialog = <FormDialog onClose={() => setOpen(false)} />;
  }

  return (
    <>
      <PermissionTooltip span name="createVerifiedDomain">
        <Button disabled={!permission} onClick={() => setOpen(true)} type="button" variant="outlined">
          Add
        </Button>
      </PermissionTooltip>
      {dialog}
    </>
  );
}
